import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AppointmentSeries,
  CreateSeries,
  CreateSeriesResult,
  EditSeries,
  EditSeriesResult,
  SeriesStatus,
} from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';

/**
 * WS7 phase 3 — recurring appointment series ("the standing Tuesday cut"). TanStack Query over the
 * generated client, mirroring data/scheduling.ts / data/services.ts (unwrap() at the boundary, a
 * mutation per write that invalidates the cached list).
 *
 * Series are renter-aware booking data: the API derives `renterId` from the owning staff member's
 * book and RLS scopes the list exactly like appointments, so a sealed renter sees only their own
 * series and the floor sees the rest — sealed-book scoping is identical to single appointments and
 * needs no special handling here.
 *
 * Query keys are defined locally (not in lib/query.ts) so this hook stays self-contained. Every
 * series write also touches appointments (occurrences are real appointments linked to the series), so
 * the mutations invalidate the broad 'appointments' prefix to refresh whatever diary day is open.
 */

const seriesKeys = {
  all: ['series'] as const,
  list: (status: SeriesStatus | undefined, staffId: string | undefined) =>
    ['series', { status: status ?? 'active', staffId: staffId ?? null }] as const,
  one: (id: string) => ['series', id] as const,
};

export interface SeriesListFilter {
  status?: SeriesStatus;
  staffId?: string;
}

export function useSeriesList(filter: SeriesListFilter = {}) {
  return useQuery({
    queryKey: seriesKeys.list(filter.status, filter.staffId),
    queryFn: async (): Promise<AppointmentSeries[]> =>
      unwrap(
        await api.GET('/v1/series', {
          params: {
            query: {
              ...(filter.status ? { status: filter.status } : {}),
              ...(filter.staffId ? { staffId: filter.staffId } : {}),
            },
          },
        }),
      ),
  });
}

export function useSeries(id: string | null) {
  return useQuery({
    queryKey: id ? seriesKeys.one(id) : ['series', 'none'],
    enabled: Boolean(id),
    queryFn: async (): Promise<AppointmentSeries> =>
      unwrap(await api.GET('/v1/series/{id}', { params: { path: { id: id! } } })),
  });
}

/**
 * Create a standing series. Returns the full conflict-aware result: the series row, the occurrences
 * that fitted (real appointments) AND the occurrences that could not be fitted. The caller MUST
 * surface the conflicts (never a silent drop) — see RecurringSeriesModal's conflict preview.
 */
export function useCreateSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateSeries): Promise<CreateSeriesResult> =>
      // The generated body type over-requires default-valued fields (intervalWeeks/depositPaid); cast
      // at the boundary — the server re-validates against the same Zod schema (matches data/staff.ts).
      unwrap(await api.POST('/v1/series', { body: body as never })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: seriesKeys.all });
      void qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

/**
 * Edit a series and apply forward: every FUTURE, not-yet-started occurrence is regenerated against
 * the diary; past and in-progress occurrences are untouched. Returns the re-fitted occurrences plus
 * any future occurrences that could not be re-fitted (a warning, never a silent drop).
 */
export function useEditSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: EditSeries }): Promise<EditSeriesResult> =>
      unwrap(
        await api.PATCH('/v1/series/{id}', {
          params: { path: { id: input.id } },
          body: input.body as never,
        }),
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: seriesKeys.all });
      void qc.invalidateQueries({ queryKey: seriesKeys.one(input.id) });
      void qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

/**
 * Detach a single occurrence from its series ("edit this one"): the appointment keeps its booking but
 * is unlinked, so a later edit-series / cancel-remaining no longer touches it. After detaching, the
 * occurrence is edited via the normal appointment path (the diary's appointment modal — Integrate
 * owns that wiring).
 */
export function useDetachOccurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      seriesId: string;
      appointmentId: string;
    }): Promise<AppointmentSeries> =>
      unwrap(
        await api.POST('/v1/series/{id}/detach', {
          params: { path: { id: input.seriesId } },
          body: { appointmentId: input.appointmentId } as never,
        }),
      ),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: seriesKeys.one(input.seriesId) });
      void qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

/**
 * Cancel the remaining (future, not-yet-started) occurrences of a series and mark the series
 * cancelled. Past and in-progress appointments are untouched (sealed history).
 */
export function useCancelSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<AppointmentSeries> =>
      unwrap(await api.POST('/v1/series/{id}/cancel', { params: { path: { id } } })),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: seriesKeys.all });
      void qc.invalidateQueries({ queryKey: seriesKeys.one(id) });
      void qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}
